import { type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, ArrowUpRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import { isEditableTarget } from '@/lib/spatial-keys.ts';

interface CommandBarProps {
  npcCommand: string;
  onOpen?: () => void;
  openLabel?: string;
  showOpen?: boolean;
  filter?: string;
  onFilterChange?: (value: string) => void;
  shortcutHint?: boolean;
  notice?: ReactNode;
}

async function copyNpc(command: string, copiedLabel: string, emptyLabel: string) {
  if (!command) {
    toast.error(emptyLabel);
    return;
  }
  try {
    await navigator.clipboard.writeText(command);
    toast.success(copiedLabel);
  } catch {
    toast.error(emptyLabel);
  }
}

export function CommandBar({
  npcCommand,
  onOpen,
  openLabel,
  showOpen = true,
  filter,
  onFilterChange,
  shortcutHint = true,
  notice,
}: CommandBarProps) {
  const { t } = useTranslation();
  const filterRef = useRef<HTMLInputElement>(null);
  const resolvedOpen = openLabel || t('dashboard.openPanel');
  const hintKey = onFilterChange
    ? 'dashboard.shortcuts'
    : onOpen
      ? 'dashboard.shortcutsNoFilter'
      : 'dashboard.shortcutsLayers';
  const hintText = t(hintKey, { action: resolvedOpen });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onFilterChange && filter) {
        event.preventDefault();
        onFilterChange('');
        return;
      }
      if (!onFilterChange) return;
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
      filterRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFilterChange, filter]);

  return (
    <div className="spatial-command-cluster">
      {onFilterChange ? (
        <div className="spatial-search">
          <Search className="spatial-search-icon" aria-hidden="true" strokeWidth={1.7} />
          <input
            ref={filterRef}
            type="search"
            value={filter ?? ''}
            onChange={(event) => onFilterChange(event.target.value)}
            placeholder={t('dashboard.filterPlaceholder')}
            className="spatial-command-input"
            aria-label={t('dashboard.filterPlaceholder')}
          />
        </div>
      ) : null}

      <div className="spatial-command" role="toolbar" aria-label={t('dashboard.commandBar')} title={hintText}>
        <span className="spatial-command-glyph" aria-hidden="true">{npcCommand.startsWith('#') ? '#' : '$'}</span>
        <code className="spatial-command-npc" title={npcCommand}>
          {(npcCommand.startsWith('#') ? npcCommand.replace(/^#\s*/, '') : npcCommand) || t('dashboard.npcIdle')}
        </code>

        {notice}

        {shortcutHint ? (
          <span className="spatial-command-hint">{hintText}</span>
        ) : null}

        <button
          type="button"
          className="spatial-command-btn spatial-command-copy"
          aria-label={t('dashboard.copyNpc')}
          onClick={() => void copyNpc(npcCommand, t('dashboard.npcCopied'), t('dashboard.npcCopyFailed'))}
        >
          <Copy className="size-4" />
          <span className="spatial-command-copy-label">{t('dashboard.copyNpc')}</span>
        </button>
        {showOpen && onOpen ? (
          <button
            type="button"
            className="spatial-command-btn spatial-command-btn-primary"
            aria-label={resolvedOpen}
            onClick={onOpen}
          >
            <ArrowUpRight className="size-4" />
            {resolvedOpen}
          </button>
        ) : null}
      </div>
    </div>
  );
}
